import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as growthActions from "../redux/actions/growthEngine";

/**
 * Shared Meta connection banner for Growth Engine / Catalog / Campaign History.
 * Reconnect always uses existing Facebook OAuth (redirect stays on campaign stepper).
 */
export const MetaConnectionBanner = ({
  metaConnectionStatus,
  isLoading = false,
  showSavedCampaignsNote = false,
}) => {
  const dispatch = useDispatch();

  const handleMetaReconnect = useCallback(() => {
    // Keep campaign-create OAuth path intact (Meta redirect_uri → /admin/growth-engine/campaign)
    dispatch(growthActions.updateFormData({ platformType: "Facebook" }));
    dispatch(growthActions.generateFacebookUrl(toast));
  }, [dispatch]);

  if (isLoading || !metaConnectionStatus) return null;

  const showMetaReconnectBanner = Boolean(metaConnectionStatus.needsReconnect);
  const showMetaExpiringBanner =
    !showMetaReconnectBanner &&
    metaConnectionStatus.tokenStatus === "expiring_soon";
  const showMetaConnectBanner =
    !metaConnectionStatus.connected && !metaConnectionStatus.needsReconnect;

  if (
    !showMetaReconnectBanner &&
    !showMetaExpiringBanner &&
    !showMetaConnectBanner
  ) {
    return null;
  }

  return (
    <div
      className={`rounded-xl mb-5 p-4 border ${
        showMetaReconnectBanner
          ? "bg-red-50 border-red-300"
          : showMetaExpiringBanner
            ? "bg-yellow-50 border-yellow-300"
            : "bg-blue-50 border-blue-300"
      }`}
      data-tour="meta-connection-banner"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <i
            className={`bi bi-exclamation-triangle-fill mt-0.5 ${
              showMetaReconnectBanner
                ? "text-red-600"
                : showMetaExpiringBanner
                  ? "text-yellow-600"
                  : "text-blue-600"
            }`}
          />
          <div>
            <div
              className={`font-semibold text-sm ${
                showMetaReconnectBanner
                  ? "text-red-700"
                  : showMetaExpiringBanner
                    ? "text-yellow-700"
                    : "text-blue-700"
              }`}
            >
              {showMetaReconnectBanner
                ? "Meta connection expired"
                : showMetaExpiringBanner
                  ? "Meta connection expiring soon"
                  : "Connect Meta to get started"}
            </div>
            <p
              className={`text-xs mt-1 ${
                showMetaReconnectBanner
                  ? "text-red-700"
                  : showMetaExpiringBanner
                    ? "text-yellow-700"
                    : "text-blue-700"
              }`}
            >
              {showMetaReconnectBanner
                ? "Reconnect your Meta account to refresh live campaign data and create new campaigns."
                : showMetaExpiringBanner
                  ? "Your Meta token expires within 7 days. Reconnect now to avoid interruption."
                  : "Link your Facebook account to create campaigns and sync live Meta analytics."}
              {showSavedCampaignsNote && showMetaReconnectBanner
                ? " Your saved campaigns are still shown below."
                : ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="text-xs font-medium text-white bg-theme_clr1 hover:opacity-90 border border-theme_clr1 rounded-lg transition-all px-4 py-[11px] whitespace-nowrap"
          onClick={handleMetaReconnect}
        >
          {showMetaConnectBanner ? "Connect Meta" : "Reconnect Meta"}
        </button>
      </div>
    </div>
  );
};

export default MetaConnectionBanner;
